/**
 * DateTimeFormatter class
 *
 * @description Utilities class to format date and time
 */
import moment from "moment";
import DateTimeUtils from "@utils/datetime.utils";

const DateTimeFormatter = {
  formats: {
    localDateTime: "YYYYMMDDHHmmss",
    localDate: "YYYYMMDD",
    year: "YYYY",
    date: "YYYY-MM-DD",
    dateTime: "YYYY-MM-DDTHH:mm:ss",
    dateTimeWithSpace: "YYYY-MM-DD HH:mm:ss",
    dateTimeWithoutSec: "YYYY-MM-DDTHH:mm",
    dateTimeAmPm: "DD MMM YYYY hh:mm A",
    dateTimeAmPmWithComma: "DD MMM YYYY, hh:mm A",
    dateOnly: "DD MMM YYYY",
    dateOnlyNoSpace: "DDMMMYYYY",
    dateOrdinal: "Do",
    monthOnly: "MMM",
    monthNumeric: "M",
    yearOnly: "YYYY",
    dateNumberOnly: "DD",
    dateNumberNoLeadingZeroOnly: "D",
    dayOfWeekOnly: "ddd",
    dateMonthOnly: "DD MMM",
    monthYearOnly: "MMM YYYY",
    monthYearOnlyWithHyphen: "YYYY-MM",
    monthShortYearOnlyWithApostrophe: "MMM'YY",
    dateNumeric: "DD/MM/YYYY",
    timeAmPm: "hh:mm A",
    time24hr: "HH:mm",
    time24hrSeconds: "HH:mm:ss",
    time24hrSecondsSpaced: "HH : mm : ss",
    time24hrMinutesSecondsSpaced: "mm : ss",
    time24hrSecondsText: "HH [HR] mm [MIN] ss [SEC]",
    hourOnly: "HH",
  },
  timeZoneSG: "Asia/Singapore",
  formatToYear: function (input) {
    return moment(input).format(this.formats.year);
  },
  formatToDateTime: function (input) {
    return moment(input).format(this.formats.dateTime);
  },
  formatToDateTimeWithSpace: function (input) {
    return moment(input).format(this.formats.dateTimeWithSpace);
  },
  formatToDateOnly: function (input) {
    return moment(input).format(this.formats.dateOnly);
  },
  formatToDateOnlyNoSpace: function (input) {
    return moment(input).format(this.formats.dateOnlyNoSpace);
  },
  formatToDateOnlyWithoutRounding: function (input) {
    return moment
      .parseZone(input)
      .subtract(8, DateTimeUtils.momentKeys.hours)
      .format(this.formats.dateOnly);
  },
  formatToLocalDateTimeOnlyWithoutRounding: function (input) {
    return moment.parseZone(input).format(this.formats.localDateTime);
  },
  formatToLocalDateTimeOnlyWithOffset: function (input) {
    return moment.parseZone(input).utcOffset("+0800").format(this.formats.localDateTime);
  },
  formatToDateNumberOnly: function (input) {
    return moment(input).format(this.formats.dateNumberOnly);
  },
  formatToDateNumberNoLeadingZeroOnly: function (input) {
    return moment(input).format(this.formats.dateNumberNoLeadingZeroOnly);
  },
  formatToDayOfWeekOnly: function (input) {
    return moment(input).format(this.formats.dayOfWeekOnly);
  },
  formatToDateMonthOnly: function (input) {
    return moment(input).format(this.formats.dateMonthOnly);
  },
  formatToMonthYearOnly: function (input) {
    return moment(input).format(this.formats.monthYearOnly);
  },
  formatToLocalDateOnly: function (input) {
    return moment(input).format(this.formats.localDate);
  },
  formatToDateNumbericOnly: function (input) {
    return moment(input).format(this.formats.dateNumeric);
  },
  formatToTimeAmPm: function (input) {
    return moment(input).format(this.formats.timeAmPm);
  },
  formatToDateTimeAmPm: function (input) {
    return moment(input).format(this.formats.dateTimeAmPm);
  },
  formatToLocalDateTime: function (input) {
    return moment(input).format(this.formats.localDateTime);
  },
  formatToDateTimeWithoutSec: function (input) {
    return moment(input).format(this.formats.dateTimeWithoutSec);
  },
  formatToTime24hr: function (input) {
    return moment(input).format(this.formats.time24hr);
  },
  formatToTime24hrSeconds: function (input) {
    return moment(input).format(this.formats.time24hrSeconds);
  },
  formatToTime24hrSecondsSpaced: function (input) {
    return moment(input).format(this.formats.time24hrSecondsSpaced);
  },
  formatToDateTimeAmPmWithComma: function (input) {
    return moment(input).format(this.formats.dateTimeAmPmWithComma);
  },
  formatLocalDateTimeToDateTimeAmPm: function (dateTime) {
    return moment(dateTime, this.formats.localDateTime).format(this.formats.dateTimeAmPm);
  },
  formatLocalDateTimeToDateTimeAmPmWithComma: function (dateTime) {
    return moment(dateTime, this.formats.localDateTime).format(this.formats.dateTimeAmPmWithComma);
  },
  formatLocalDateTimeToDateTime: function (dateTime) {
    return moment(dateTime, this.formats.localDateTime).format(this.formats.dateTime);
  },
  formatDateTimeToDateTimeAmPm: function (dateTime) {
    return moment(dateTime, this.formats.dateTime).format(this.formats.dateTimeAmPm);
  },
  formatLocalDateTimeToDateOnly: function (dateTime) {
    return moment(dateTime, this.formats.localDateTime).format(this.formats.dateOnly);
  },
  formatLocalDateTimeToTimeAmPm: function (dateTime) {
    return moment(dateTime, this.formats.localDateTime).format(this.formats.timeAmPm);
  },
  formatLocalDateToMonthOnly: function (date) {
    return moment(date, this.formats.localDate).format(this.formats.monthOnly);
  },
  formatLocalDateToMonthNumericOnly: function (date) {
    return moment(date, this.formats.localDate).format(this.formats.monthNumeric);
  },
  formatLocalDateToYearOnly: function (date) {
    return moment(date, this.formats.localDate).format(this.formats.yearOnly);
  },
  formatLocalDateTimeToDate: function (dateTime) {
    return moment(dateTime, this.formats.localDateTime).format(this.formats.date);
  },
  formatDateTimeAmPmToLocalDateTime: function (dateTime) {
    return moment(dateTime, this.formats.dateTimeAmPm).format(this.formats.localDateTime);
  },
  formatDateTimeAmPmToDateTime: function (dateTime) {
    return moment(dateTime, this.formats.dateTimeAmPm).format(this.formats.dateTime);
  },
  formatTimeAmPmToTime24hr: function (timeAmPm) {
    return moment(timeAmPm, this.formats.timeAmPm).format(this.formats.time24hr);
  },
  formatTime24hrToTimeAmPm: function (time24hr) {
    return moment(time24hr, this.formats.time24hr).format(this.formats.timeAmPm);
  },
  formatMonthYearOnlyToMonthYearOnlyWithHyphen: function (date) {
    return moment(date, this.formats.monthYearOnly).format(this.formats.monthYearOnlyWithHyphen);
  },
  formatLocalDateToMonthShortYearOnlyWithApostrophe: function (date) {
    return moment(date, this.formats.localDate).format(
      this.formats.monthShortYearOnlyWithApostrophe,
    );
  },
  formatToHourOnly: function (input, inputFormat) {
    return moment(input, inputFormat).format(this.formats.hourOnly);
  },
  /**
   * Returns string of time in minutes and seconds.
   * @param {String} string - Time in a ISO_8601 format ("PT1H30M").
   * @example
   * DateTimeFormatter.formatTimeToSeconds("PT1H30M");
   * @return {Number} - Number of time in seconds (5400).
   */
  formatTimeToSeconds: function (time) {
    let newTime = moment.duration(time, moment.ISO_8601);
    return newTime.asSeconds();
  },
  /**
   * Returns string of time in minutes and seconds.
   * @param {Number} seconds - Time in seconds.
   * @example
   * DateTimeFormatter.formatTimeInSecondToMinuteSecond(210);
   * @return {String} - String of time in minutes/seconds (3 min 30 sec, 30 sec, 1 min).
   */
  formatTimeInSecondToMinuteSecond: function (seconds) {
    if (!seconds) return 0;
    const formatted = moment.duration(seconds, "seconds");
    let formattedMinutes = parseInt(formatted.asMinutes());
    let formattedSeconds = moment.utc(formatted.asMilliseconds()).format("s");
    let duration = `${formattedMinutes > 0 ? formattedMinutes + " min" : ""} ${
      formattedSeconds > 0 ? formattedSeconds + " sec" : ""
    }`;
    return duration;
  },
  formatTimeInSecondToHourMinuteSecond: function (seconds, options = {}) {
    // default options
    const { hideZeroHour = true, hideZeroMin = true, hideZeroSec = true } = options;
    if (!seconds) return 0;
    const formatted = moment.duration(seconds, "seconds");
    let formattedHours = formatted.hours();
    let formattedMinutes = formatted.minutes();
    let formattedSeconds = formatted.seconds();
    // let duration = `${hideZeroHour && formattedHours > 0 ? formattedHours + " hr" : ""} ${
    //   formattedMinutes > 0 ? formattedMinutes + " min" : ""
    // } ${formattedSeconds > 0 ? formattedSeconds + " sec" : ""}`;
    const hour = `${hideZeroHour && formattedHours <= 0 ? "" : formattedHours + " hr"}`;
    const min = `${hideZeroMin && formattedMinutes <= 0 ? "" : formattedMinutes + " min"}`;
    const sec = `${hideZeroSec && formattedSeconds <= 0 ? "" : formattedSeconds + " sec"}`;
    return `${hour} ${min} ${sec}`;
  },
  formatSecondsToTime24hrSeconds(seconds) {
    return moment().startOf("day").seconds(seconds).format(this.formats.time24hrSeconds);
  },
  formatSecondsToTime24hrSecondsSpacedText(seconds) {
    return moment().startOf("day").seconds(seconds).format(this.formats.time24hrSecondsSpaced);
  },
  formatSecondsToTime24hrMinutesSecondsSpaced(seconds) {
    return moment()
      .startOf("day")
      .seconds(seconds)
      .format(this.formats.time24hrMinutesSecondsSpaced);
  },
  formatSecondsToTime24hrSecondsText(seconds) {
    return moment().startOf("day").seconds(seconds).format(this.formats.time24hrSecondsText);
  },
  /**
   * Insert input datetime with its format, and convert to desired datetime format
   * @param {String} input current datetime string
   * @param {String} inputFormat input datetime format
   * @param {String} outputFormat desired output datetime format
   * @returns {String} desired datetime format
   */
  formatDateTime(input, inputFormat, outputFormat) {
    return moment(input, inputFormat).format(outputFormat);
  },
  formatToNthDay(input, inputFormat) {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const nth = ["First", "Second", "Third", "Fourth", "Fifth"];
    const dateTime = moment(input, inputFormat);
    const date = dateTime.date();
    const day = dateTime.day();
    const n = Math.ceil(date / 7);

    return nth[n - 1] + " " + days[day];
  },
  formatLocalDateTimeToDateTimeWithoutSec(input) {
    return moment(input, this.formats.localDateTime).format(this.formats.dateTimeWithoutSec);
  },
};

export default DateTimeFormatter;
